import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, 
  ListItemText, Divider, Grid 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import superAdminService from '../services/superAdminService';

const SuperAdminResponses = () => {
  const navigate = useNavigate();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [open, setOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const data = await superAdminService.getAllResponses();
        setResponses(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load responses", error);
      } finally {
        setLoading(false);
      }
    };
    fetchResponses();
  }, []);

  const handleOpen = (response) => {
    setSelectedResponse(response);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedResponse(null);
  };

  if (loading) return <CircularProgress sx={{ m: 5 }} />;

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/super-admin/dashboard')} sx={{ mb: 3 }}>
        Back to Dashboard
      </Button>
      
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Survey Responses
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Survey</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Dealer</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {responses.map((res) => (
              <TableRow key={res.id || res.Id} hover>
                <TableCell>
                    {new Date(res.submittedAt || res.SubmittedAt).toLocaleDateString()}
                    <Typography variant="caption" display="block" color="text.secondary">
                        {new Date(res.submittedAt || res.SubmittedAt).toLocaleTimeString()}
                    </Typography>
                </TableCell>
                
                <TableCell sx={{ fontWeight: 'bold' }}>
                    {res.survey?.title || res.Survey?.Title || 'Untitled'}
                </TableCell>

                <TableCell>
                    <Chip 
                        label={res.survey?.dealerName || res.Survey?.DealerName || 'Unknown'} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                    />
                </TableCell>

                <TableCell>
                    {res.customer ? (
                        <>
                            {res.customer.name || res.Customer.Name}
                            <Typography variant="caption" display="block" color="text.secondary">
                                {res.customer.email || res.Customer.Email}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            Anonymous
                        </Typography>
                    )}
                </TableCell>

                <TableCell align="center">
                    <Button 
                        variant="contained" 
                        size="small" 
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpen(res)}
                        sx={{ bgcolor: '#a8851d', '&:hover': { bgcolor: '#8c6e18' } }}
                    >
                        View Answers
                    </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MODAL FOR ANSWERS */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold">Response Details</Typography>
                <Typography variant="caption" color="text.secondary">
                    ID: {selectedResponse?.id || selectedResponse?.Id}
                </Typography>
            </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
            {selectedResponse && (
                <>
                    {/* Customer Info Section */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fdfbf7' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">CUSTOMER</Typography>
                                <Typography fontWeight="bold">
                                    {selectedResponse.customer?.name || selectedResponse.Customer?.Name || 'Anonymous'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">DEALER</Typography>
                                <Typography fontWeight="bold">
                                    {selectedResponse.survey?.dealerName || selectedResponse.Survey?.DealerName}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Typography variant="h6" sx={{ mb: 2, color: '#a8851d', fontWeight: 'bold' }}>
                        Q&A Submission
                    </Typography>

                    <List>
                        {(selectedResponse.answers || selectedResponse.Answers || []).map((ans, idx) => (
                            <React.Fragment key={idx}>
                                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                    <ListItemText 
                                        primary={
                                            <Typography fontWeight="600" color="#334155" sx={{ mb: 0.5 }}>
                                                {ans.questionText || ans.QuestionText}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography 
                                                component="span" 
                                                variant="body1" 
                                                color="black"
                                                sx={{ 
                                                    display: 'block', 
                                                    bgcolor: '#f1f5f9', 
                                                    p: 1.5, 
                                                    borderRadius: 1,
                                                    borderLeft: '4px solid #a8851d'
                                                }}
                                            >
                                                {ans.answerText || ans.AnswerText}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                {idx < (selectedResponse.answers?.length - 1) && <Divider component="li" />}
                            </React.Fragment>
                        ))}
                    </List>
                </>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={handleClose} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminResponses;